import { useCallback } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { commonStyles } from 'features/cart/styles';
import { useAddToCart } from 'features/menu/components/product-wizard/use-add-to-cart';
import {
  initializeSelectedProduct,
  initializeSlotSelections,
} from 'features/menu/components/product-wizard/utils';
import { Pressable, StyleSheet, View } from 'react-native';
import { Typography } from 'ucl';

import Picture from 'components/picture';
import { useFormatCalories } from 'hooks/use-format-calories';
import { t } from 'state/intl/translations';
import { useUIContext } from 'state/ui';
import { ILocalizedMenuEntry } from 'state/unified-menu/types';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { isCombo, mapImageResourceToSanityImage } from 'state/unified-menu/utils';
import { getDesignToken, primitive } from 'styles/constants';
import { HapticsImpactStyle, hapticImpact } from 'utils/haptic';

export const CartUpsellItem = ({
  menuEntry,
  isLoading,
}: {
  menuEntry: ILocalizedMenuEntry;
  isLoading?: boolean;
}) => {
  const { posServiceMode } = useMenuQueryOptions();
  const formatCalories = useFormatCalories();
  const { formatCurrencyForLocale } = useUIContext();
  const addToCart = useAddToCart();
  const { indexedMenu } = useUnifiedMenuContext();

  // TODO: Log amplitude event

  const image = mapImageResourceToSanityImage(
    menuEntry?.image?.resource ?? '',
    menuEntry?.image?.altText?.locale ?? ''
  );

  const upsellItemLabel = t('addItemLabel', { itemName: menuEntry.name?.locale });
  const price = formatCurrencyForLocale(menuEntry.price?.[posServiceMode]);
  const calories = formatCalories(menuEntry.nutrition.calories.def);

  const handleAddToCart = useCallback(
    ({ menuEntry }: { menuEntry: ILocalizedMenuEntry }) => {
      if (isLoading) {
        return;
      }
      hapticImpact({ style: HapticsImpactStyle.Light });
      const selectedProduct =
        initializeSelectedProduct({
          menu: indexedMenu,
          item: menuEntry,
          posServiceMode,
        }) ?? menuEntry;

      // Build default slot and modifier selections
      const slotsSelections = initializeSlotSelections(
        selectedProduct,
        indexedMenu,
        isCombo(selectedProduct),
        posServiceMode
      );

      // TODO: Log amplitude event
      addToCart(
        menuEntry,
        1,
        slotsSelections,
        `/menu/${menuEntry.type.toLowerCase()}/${menuEntry.id}`
      );
    },
    [addToCart, indexedMenu, isLoading, posServiceMode]
  );

  return (
    <Pressable
      style={styles.container}
      disabled={isLoading}
      onPress={() => handleAddToCart({ menuEntry })}
      testID={`upsell-add-to-cart`}
    >
      <Picture
        key={image.asset?._id || menuEntry.id}
        size="$16"
        alt={image.locale?.imageDescription ?? ''}
        image={image}
        testID={`upsell-image`}
      />
      <View style={styles.details}>
        <Typography
          style={[commonStyles.cartEntryItemName, styles.name]}
          numberOfLines={2}
          ellipsizeMode="tail"
          testID={`upsell-name`}
        >
          {menuEntry.name?.locale}
        </Typography>
        <Typography style={commonStyles.cartEntryBody} testID={`upsell-price-and-calories`}>
          {price} • {calories}
        </Typography>
      </View>
      <Icon
        variant="bkUpsellPlusV2"
        title={upsellItemLabel}
        accessibilityRole="button"
        accessibilityLabel={upsellItemLabel}
        color={isLoading ? primitive.$blackOpacity50 : primitive.$bbqBrown}
        size="30"
        marginLeft="$2"
        testID={`upsell-add-to-cart-icon`}
      />
    </Pressable>
  );
};

const styles = StyleSheet.create({
  container: {
    width: 288,
    height: 96,
    backgroundColor: getDesignToken('background-card'),
    borderWidth: 0.5,
    borderColor: primitive.$blackOpacity30,
    borderRadius: primitive.$borderRadius,
    marginRight: 16,
    alignItems: 'center',
    justifyContent: 'space-around',
    flexDirection: 'row',
    paddingHorizontal: 12,
    overflow: 'hidden',
  },
  details: {
    flexDirection: 'column',
    gap: 2,
    flex: 1,
    minWidth: 0,
    paddingHorizontal: 4,
  },
  name: {
    flexShrink: 1,
  },
});
